import { Fragment, ReactNode, useEffect, useId, useRef, useState } from "react";
import { clsx } from "clsx";
import {
    ChevronRightIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/16/solid";

export const PICKER_ROW =
    "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-1.5";
export const PICKER_RULE = "border-zinc-950/5 dark:border-white/10";

const ROW_ACTIVE = "bg-zinc-950/5 dark:bg-white/10";
const COLUMN_TITLE = "px-4 pb-1 pt-3 text-xs font-medium text-subtle";

/**
 * A choice in two steps: groups on the left, the items of the one under the
 * pointer on the right, and a search over both. Picking an item is the only
 * way out, so the caller closes whatever holds this. Mount it while open, so
 * every opening starts from a clean search with the current item in view.
 */
export default function TwoColumnPicker<Group extends { key: string }, Item>({
    groups,
    itemsOf,
    itemKey,
    initialGroupKey,
    initialItemKey,
    filterGroups,
    onPick,
    searchLabel,
    groupsTitle,
    itemsTitle,
    renderGroup,
    renderItem,
    itemHeading,
    groupFooter,
    footer,
}: {
    groups: Group[];
    itemsOf: (group: Group) => Item[];
    itemKey: (item: Item) => string;
    initialGroupKey?: string;
    initialItemKey?: string;
    filterGroups: (groups: Group[], query: string) => Group[];
    onPick: (item: Item) => void;
    searchLabel: string;
    groupsTitle: string;
    itemsTitle: string;
    renderGroup: (group: Group) => ReactNode;
    renderItem: (item: Item) => ReactNode;
    /** A title over the items that share it, when a group's items come in kinds. */
    itemHeading?: (item: Item) => string | undefined;
    /** Below the items of the group in view. */
    groupFooter?: (group: Group) => ReactNode;
    /** Below both columns. */
    footer?: ReactNode;
}) {
    const listId = useId();
    const groupsRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);

    const [query, setQuery] = useState("");
    const [activeGroupKey, setActiveGroupKey] = useState(initialGroupKey);
    const [activeItemKey, setActiveItemKey] = useState(initialItemKey);

    const shown = filterGroups(groups, query);
    // Both highlights follow the search: what was active may be filtered out.
    const activeGroup =
        shown.find((group) => group.key === activeGroupKey) ?? shown[0];
    const shownItems = activeGroup ? itemsOf(activeGroup) : [];
    const activeItem =
        shownItems.find((item) => itemKey(item) === activeItemKey) ??
        shownItems[0];
    const activeItemId =
        activeItem === undefined ? undefined : itemKey(activeItem);

    useEffect(() => {
        groupsRef.current
            ?.querySelector("[data-active=true]")
            ?.scrollIntoView({ block: "nearest" });
    }, [activeGroup?.key]);

    useEffect(() => {
        itemsRef.current
            ?.querySelector("[data-active=true]")
            ?.scrollIntoView({ block: "nearest" });
    }, [activeItemId]);

    const move = (step: number) => {
        if (activeItem === undefined) return;
        const index = shownItems.indexOf(activeItem);
        const next =
            shownItems[(index + step + shownItems.length) % shownItems.length];
        if (next !== undefined) setActiveItemKey(itemKey(next));
    };

    return (
        <div
            className={"flex w-screen max-w-2xl flex-col text-sm text-default"}
        >
            <div
                className={clsx(
                    "flex items-center gap-2 border-b px-3 py-2.5",
                    PICKER_RULE,
                )}
            >
                <MagnifyingGlassIcon
                    className={"size-4 shrink-0 text-subtle"}
                    aria-hidden={"true"}
                />
                <input
                    autoFocus
                    type={"text"}
                    role={"combobox"}
                    aria-label={searchLabel}
                    aria-expanded={true}
                    aria-controls={listId}
                    aria-activedescendant={
                        activeItemId === undefined
                            ? undefined
                            : `${listId}-${activeItemId}`
                    }
                    value={query}
                    placeholder={`${searchLabel}…`}
                    className={
                        "w-full border-0 bg-transparent p-0 text-sm text-default placeholder:text-subtle focus:outline-none focus:ring-0"
                    }
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            move(1);
                        } else if (event.key === "ArrowUp") {
                            event.preventDefault();
                            move(-1);
                        } else if (
                            event.key === "Enter" &&
                            activeItem !== undefined
                        ) {
                            event.preventDefault();
                            onPick(activeItem);
                        }
                    }}
                />
            </div>

            {activeGroup ? (
                <div className={clsx("flex h-80", PICKER_RULE)}>
                    <div className={"flex w-1/2 min-w-0 flex-col"}>
                        <div className={COLUMN_TITLE}>{groupsTitle}</div>
                        <div
                            ref={groupsRef}
                            className={"min-h-0 flex-1 overflow-y-auto p-1"}
                        >
                            {shown.map((group) => {
                                const isActive = group.key === activeGroup.key;
                                return (
                                    <div
                                        key={group.key}
                                        data-active={isActive}
                                        className={clsx(
                                            PICKER_ROW,
                                            isActive && ROW_ACTIVE,
                                        )}
                                        onMouseEnter={() =>
                                            setActiveGroupKey(group.key)
                                        }
                                        onClick={() =>
                                            setActiveGroupKey(group.key)
                                        }
                                    >
                                        {renderGroup(group)}
                                        <ChevronRightIcon
                                            className={clsx(
                                                "ml-auto size-4 shrink-0 text-subtle",
                                                !isActive && "invisible",
                                            )}
                                            aria-hidden={"true"}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div
                        className={clsx(
                            "flex w-1/2 min-w-0 flex-col border-l",
                            PICKER_RULE,
                        )}
                    >
                        <div className={COLUMN_TITLE}>{itemsTitle}</div>
                        <div
                            ref={itemsRef}
                            id={listId}
                            role={"listbox"}
                            aria-label={itemsTitle}
                            className={"min-h-0 flex-1 overflow-y-auto p-1"}
                        >
                            {shownItems.map((item, index) => {
                                const key = itemKey(item);
                                const isActive = key === activeItemId;
                                const heading = itemHeading?.(item);
                                const previous = shownItems[index - 1];
                                const startsHeading =
                                    heading !== undefined &&
                                    (previous === undefined ||
                                        itemHeading?.(previous) !== heading);
                                return (
                                    <Fragment key={key}>
                                        {startsHeading && (
                                            <div className={COLUMN_TITLE}>
                                                {heading}
                                            </div>
                                        )}
                                        <div
                                            id={`${listId}-${key}`}
                                            role={"option"}
                                            aria-selected={isActive}
                                            data-active={isActive}
                                            className={clsx(
                                                PICKER_ROW,
                                                isActive && ROW_ACTIVE,
                                            )}
                                            onMouseEnter={() =>
                                                setActiveItemKey(key)
                                            }
                                            onClick={() => onPick(item)}
                                        >
                                            {renderItem(item)}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                        {groupFooter?.(activeGroup)}
                    </div>
                </div>
            ) : (
                <p
                    className={
                        "flex h-80 items-center justify-center px-3 text-subtle"
                    }
                >
                    Nothing matches “{query.trim()}”.
                </p>
            )}

            {footer}
        </div>
    );
}
